import io from 'socket.io-client';

class SocketWorker {
  constructor(companyId, userId) {
    if (
      !SocketWorker.instance ||
      SocketWorker.instance.companyId !== companyId ||
      SocketWorker.instance.userId !== userId
    ) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.eventListeners = {};
      this.pendingEvents = [];
      this.isConnecting = false;
      this.configureSocket();
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    if (this.socket) {
      return;
    }

    this.isConnecting = true;

    const url = `${import.meta.env.VITE_BACKEND_URL}/${this.companyId}`;

    this.socket = io(url, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId },
      transports: ['websocket'],
    });

    this.socket.on('connect', () => {
      console.info('Socket conectado');
      this.isConnecting = false;
      this.processPendingEvents();
    });

    this.socket.on('connect_error', (error) => {
      console.error('Erro de conexão:', error);
      this.isConnecting = false;
    });

    this.socket.on('disconnect', (reason) => {
      console.info('Socket desconectado:', reason);
      this.isConnecting = false;
    });

    this.socket.on('reconnect', () => {
      console.info('Socket reconectado');
      this.processPendingEvents();
    });
  }

  processPendingEvents() {
    if (this.pendingEvents.length > 0 && this.socket && this.socket.connected) {
      const events = [...this.pendingEvents];
      this.pendingEvents = [];
      events.forEach(({ event, data }) => {
        this.socket.emit(event, data);
      });
    }
  }

  on(event, callback) {
    if (!this.socket) {
      console.warn(`Socket não inicializado para evento: ${event}`);
      this.configureSocket();
    }

    const wrappedCallback = (...args) => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Erro no evento ${event}:`, error);
      }
    };

    this.socket.on(event, wrappedCallback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(wrappedCallback);

    return wrappedCallback;
  }

  off(event, callback) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
      if (this.eventListeners[event]) {
        const index = this.eventListeners[event].indexOf(callback);
        if (index !== -1) {
          this.eventListeners[event].splice(index, 1);
        }
      }
    } else {
      this.socket.off(event);
      delete this.eventListeners[event];
    }
  }

  emit(event, data) {
    if (!this.socket) {
      this.configureSocket();
      this.pendingEvents.push({ event, data });
      return;
    }

    if (this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      this.pendingEvents.push({ event, data });
      if (!this.isConnecting) {
        this.connect();
      }
    }
  }

  disconnect() {
    if (this.socket) {
      Object.keys(this.eventListeners).forEach((event) => {
        this.eventListeners[event].forEach((callback) => {
          this.socket.off(event, callback);
        });
      });
      this.eventListeners = {};
      this.pendingEvents = [];
      this.socket.disconnect();
      this.socket = null;
      SocketWorker.instance = null;
      console.info('Socket desconectado');
    }
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected && !this.isConnecting) {
      this.socket.connect();
    }
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }

  getSocket() {
    return this.socket;
  }
}

const instance = (companyId, userId) => {
  if (!companyId || !userId) {
    console.error('companyId e userId são obrigatórios');
    return null;
  }
  return new SocketWorker(companyId, userId);
};

export default instance;
